import { NavLink, useLocation } from 'react-router-dom'
import { useMe } from '@/features/auth/hooks/useAuth'
import { userHasPermission } from '@/lib/permissions'
import { useState, type ReactNode } from 'react'
import {
    LayoutDashboard,
    Globe,
    Box,
    Play,
    UserCheck,
    Layers,
    ClipboardCheck,
    Inbox,
    BookOpen,
    Users,
    KeyRound,
    UsersRound,
    Contact,
    LogIn,
    CalendarClock,
    Vault,
    MapPin,
    Bot,
    Library,
    Tags,
    LayoutTemplate,
    Cpu,
    Image as ImageIcon,
    Shapes as ShapesIcon,
    BookMarked,
    SlidersHorizontal,
    ScrollText,
    UserCog,
    Settings,
    ChevronDown,
    Network,
    Microscope,
    ArrowDownToLine,
    Code2,
    ShieldAlert,
    ShieldCheck,
    CloudCog,
    Gauge,
} from 'lucide-react'
import { useIsAuthenticated } from '@/app/store/auth'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { useAppType } from '@/hooks/useAppType'
import { useQuery } from '@tanstack/react-query'
import { servicenowCmdbApi } from '@/features/servicenow-cmdb/api'
import api from '@/lib/api/axios'


export function Sidebar() {
    const location = useLocation()
    const isAuthenticated = useIsAuthenticated()
    // Permissions live on the /auth/me user, not the store's login-shaped one (lib/permissions.ts).
    const { data: me } = useMe()
    const { t } = useTranslation()
    const { appType, isLoading: appTypeLoading } = useAppType()
    const [isHovered, setIsHovered] = useState(false)
    // Which of the Admin and Settings submenus are open, remembered per browser. Absent means
    // "not chosen yet", and then a group opens only when the page you are on is inside it.
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
        try { return JSON.parse(localStorage.getItem('sidebar.openGroups') || '{}') } catch { return {} }
    })
    const toggleGroup = (key: string, open: boolean) => setOpenGroups((g) => {
        const next = { ...g, [key]: !open }
        try { localStorage.setItem('sidebar.openGroups', JSON.stringify(next)) } catch { /* private window */ }
        return next
    })

    // Sidebar is always collapsed by default, expands on hover
    const sidebarCollapsed = !isHovered

    // Check for pending CMDB updates (new/changed items)
    const { data: cmdbPendingData } = useQuery({
        queryKey: ['cmdb-pending-count'],
        queryFn: async () => {
            const res = await servicenowCmdbApi.getPendingCount()
            return res.data?.data?.pendingCount ?? 0
        },
        // The badge on the ServiceNow entry: asked only by those who can see that entry.
        enabled: isAuthenticated && userHasPermission(me, 'servicenow.read'),
        refetchInterval: 60000, // Poll every 60 seconds
    })
    const cmdbHasUpdates = (cmdbPendingData ?? 0) > 0

    // A scan status change is waiting for a second person, and this person could be the one: the
    // marker on the Scan status changes entry. Their own requests do not count - they cannot
    // approve those, so there is nothing here for them to do.
    const { data: statusChangesWaiting } = useQuery({
        queryKey: ['scan-status-changes', 'waiting-for-me', me?.id],
        queryFn: async () => {
            const res = await api.get<{ data: Array<{ requestedBy: { id: number } | null }> }>('/api/scan-status-changes', { params: { state: 'pending' } })
            return (res.data?.data ?? []).filter((c) => Number(c.requestedBy?.id) !== Number(me?.id)).length
        },
        enabled: isAuthenticated && userHasPermission(me, 'scan-status.approve'),
        refetchInterval: 60000,
    })
    const statusChangeWaiting = (statusChangesWaiting ?? 0) > 0

    if (!isAuthenticated) {
        return null
    }

    /**
     * Each entry carries the permission its page checks (router.tsx, docs/rbac-permissions.md) and
     * shows only to those who hold it - never by role name, so a custom role sees exactly the pages
     * it was granted and nobody is offered a page that answers "Access Denied". A section with no
     * entries left is not shown at all.
     */
    type NavEntry = { name: string; href: string; icon: typeof Play; permission: string }
    const permitted = (items: NavEntry[]) => items.filter((item) => userHasPermission(me, item.permission))

    // ── OPERATE section ──
    const operateItems = permitted([
        { name: t('navigation.dashboard'), href: '/', icon: LayoutDashboard, permission: 'dashboard.read' },
        { name: t('navigation.objects'), href: '/objects', icon: Box, permission: 'objects.read' },
        { name: t('navigation.planningCanvas'), href: '/visualization', icon: Network, permission: 'canvas.view' },
        // Its own entry rather than a child of the canvas. The Gantt is the first of several
        // readings of a plan's readiness — wave table, executive summary, RTO waterfall,
        // readiness matrix — so the nav names the question, not the one chart that answers it
        // today. Nesting it under the canvas also buried it behind a group you had to expand.
        { name: t('navigation.readiness', 'Readiness'), href: '/schedule', icon: Gauge, permission: 'recover-requests.read' },
        { name: t('navigation.ingressMonitoring'), href: '/ingress-monitoring', icon: ArrowDownToLine, permission: 'analytics.read' },
        { name: t('navigation.forensics'), href: '/forensics', icon: Microscope, permission: 'forensics.read' },
        { name: t('navigation.analyticsAnomaly', 'Analytics & Anomaly'), href: '/analytics', icon: ShieldAlert, permission: 'analytics.read' },
        { name: t('navigation.scanStatusChanges'), href: '/scan-status-changes', icon: ShieldCheck, permission: 'scan-status.approve' },
        { name: t('navigation.iac', 'IaC'), href: '/iac', icon: Code2, permission: 'iac.use' },
        { name: t('navigation.servicenowCmdb', 'ServiceNow CMDB'), href: '/servicenow-cmdb', icon: CloudCog, permission: 'servicenow.read' },
    ])

    // ── RECOVERY MANAGEMENT and MY ASSIGNMENTS sections ──
    // Flat lists under their own headings, each page with its own icon, the same as Operate.
    // Andrew, 2026-09-24: "in recover is a list of options the same as we have in operate without
    // the submenus of recovery manager and my assignments." They used to be two collapsible groups
    // under one RECOVER heading, which put every page one click deeper than the Operate ones and,
    // with the sidebar collapsed, left only a group icon that went nowhere.
    const recoveryItems = permitted([
        { name: t('navigation.recoveryScenarios'), href: '/recovery-scenarios', icon: Layers, permission: 'recover-requests.update' },
        // Assessed before recoveries are requested, so before Recovery Manager (Andrew, 13 Oct 2026).
        { name: t('recoverRequests.mobilisation.impactAssessment', 'Impact Assessment'), href: '/impact-assessment', icon: ShieldAlert, permission: 'recover-requests.read' },
        { name: t('navigation.recoveryManagement'), href: '/recovery-management', icon: Play, permission: 'recover-requests.read' },
        { name: t('navigation.requestApprovals'), href: '/request-approvals', icon: ClipboardCheck, permission: 'recovery.approve' },
        { name: t('navigation.assignEngineer'), href: '/assign-engineer', icon: UserCheck, permission: 'recover-requests.update' },
    ])
    const assignmentItems = permitted([
        { name: t('navigation.newAssignments'), href: '/my-assignments', icon: Inbox, permission: 'recover-requests.read' },
        { name: t('navigation.updateRunBook'), href: '/update-runbook', icon: BookOpen, permission: 'recover-requests.read' },
    ])

    /** One link with its icon: the Operate style, used by every section that lists pages directly. */
    const navItem = (item: NavEntry) => {
        const isActive = location.pathname === item.href
        const Icon = item.icon
        const showDot = (item.href === '/servicenow-cmdb' && cmdbHasUpdates) || (item.href === '/scan-status-changes' && statusChangeWaiting)

        return (
            <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setIsHovered(false)}
                className={cn(
                    'flex items-center space-x-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                    !isActive && 'hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-white/10 dark:hover:text-blue-300',
                    isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-sidebar-foreground',
                )}
                title={sidebarCollapsed ? item.name : undefined}
            >
                <span className="relative flex-shrink-0">
                    <Icon className="h-5 w-5" />
                    {showDot && (
                        <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                        </span>
                    )}
                </span>
                {!sidebarCollapsed && <span className="whitespace-nowrap">{item.name}</span>}
            </NavLink>
        )
    }

    /**
     * A section heading. Collapsed, the TEXT goes but the space stays: Andrew, 2026-09-25, "when the
     * menu is collapsed to have spaces where the separators are so the icons align". Removing the
     * headings moved every icon beneath them up, so an icon sat in a different place collapsed from
     * expanded and the menu jumped as it opened on hover.
     */
    const sectionHeading = (text: string, first = false) => (
        <div
            aria-hidden={sidebarCollapsed || undefined}
            className={cn(
                'overflow-hidden whitespace-nowrap px-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground',
                first ? 'py-1' : 'pt-4 pb-1',
                sidebarCollapsed && 'invisible',
            )}
        >
            {text}
        </div>
    )

    /**
     * A submenu: one row that opens to show its pages. Andrew, 2026-09-24: "the admin menu option
     * should be a sub menu of admin ... the settings menu option should be a sub menu of settings"
     * — the two long, rarely-visited lists fold away under one row each, so the menu stays short
     * and Operate and Recovery stay in view. Closed until opened, unless the current page is in it.
     */
    const navGroup = (key: string, label: string, Icon: typeof Play, items: { href: string }[], children: ReactNode) => {
        const containsCurrent = items.some((i) => location.pathname === i.href)
        const open = openGroups[key] ?? containsCurrent
        // The same rows whether the menu is collapsed or not — collapsed only drops the words and the
        // indent — so every icon, including those of an open group's pages, stays where it is.
        return (
            <div className="space-y-0.5">
                <button
                    type="button"
                    aria-expanded={open}
                    aria-label={sidebarCollapsed ? label : undefined}
                    title={sidebarCollapsed ? label : undefined}
                    onClick={() => toggleGroup(key, open)}
                    className={cn(
                        'flex items-center justify-between w-full rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                        'hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-white/10 dark:hover:text-blue-300',
                        // Collapsed and shut, the group icon is the only sign the current page is in it.
                        sidebarCollapsed && !open && containsCurrent
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                            : 'text-sidebar-foreground',
                    )}
                >
                    <span className="flex items-center space-x-3">
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        {!sidebarCollapsed && <span className="whitespace-nowrap">{label}</span>}
                    </span>
                    {!sidebarCollapsed && <ChevronDown className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')} />}
                </button>
                {open && (
                    <div className={cn('space-y-0.5', !sidebarCollapsed && 'ml-6 border-l-2 border-gray-200 pl-2 dark:border-white/10')}>
                        {children}
                    </div>
                )}
            </div>
        )
    }

    // ── ADMIN and SETTINGS sections ──
    // The groups under SETTINGS are static labels, deliberately not collapsible: more collapse
    // states inside a sidebar that already collapses is more than a person should have to track.
    // ADMIN keeps the screens about people; everything that configures the application moved to
    // SETTINGS (Andrew, 2026-09-24: "Leave the user screens in admin on the main menu and move the
    // others to a new heading called settings"). Both are plain lists with an icon per page, like
    // the sections above them. Settings keeps its small group labels: thirteen pages in one run is
    // past the point where people scan rather than read.
    const adminItems = permitted([
        { name: t('navigation.users'), href: '/users', icon: Users, permission: 'users.update' },
        { name: t('navigation.roles'), href: '/roles', icon: KeyRound, permission: 'roles.update' },
        { name: t('navigation.teams'), href: '/teams', icon: UsersRound, permission: 'teams.read' },
        // During a mobilisation: a team's lead or deputy lets their people in (features/mobilisation).
        { name: t('mobilisation.myTeam.nav'), href: '/my-team', icon: UserCheck, permission: 'mobilisation.confirm' },
        { name: t('navigation.contacts'), href: '/contacts', icon: Contact, permission: 'contacts.read' },
        { name: t('navigation.loginActivity', 'Login Activity'), href: '/login-activity', icon: LogIn, permission: 'system.admin' },
    ])
    const settingsSections = [
        {
            key: 'infrastructure',
            label: t('navigation.adminSection.infrastructure', 'Infrastructure'),
            items: permitted([
                { name: t('navigation.schedulers', 'Schedulers'), href: '/schedulers', icon: CalendarClock, permission: 'schedulers.read' },
                { name: t('navigation.vaults'), href: '/vaults', icon: Vault, permission: 'vaults.read' },
                { name: t('navigation.locations'), href: '/locations', icon: MapPin, permission: 'locations.read' },
            ]),
        },
        {
            key: 'ai',
            label: t('navigation.adminSection.ai', 'AI'),
            items: permitted([
                { name: t('navigation.aiModels'), href: '/ai-models', icon: Bot, permission: 'system.admin' },
                // The customer's own knowledge, used by the AI agents (features/knowledge-base). The
                // agents' prompts are VaultSentinel's and have no screen.
                { name: t('knowledgeBase.nav', 'Knowledge Base'), href: '/knowledge-base', icon: Library, permission: 'knowledge.read' },
            ]),
        },
        {
            key: 'configuration',
            label: t('navigation.adminSection.configuration', 'Configuration'),
            items: permitted([
                { name: t('navigation.classifications', 'Classifications'), href: '/classifications', icon: Tags, permission: 'classifications.read' },
                { name: t('navigation.templates', 'Templates'), href: '/templates', icon: LayoutTemplate, permission: 'templates.read' },
                { name: t('navigation.technologies'), href: '/technologies', icon: Cpu, permission: 'technologies.read' },
                { name: t('navigation.icons', 'Icons'), href: '/icons', icon: ImageIcon, permission: 'graphics.read' },
                { name: t('navigation.shapes', 'Graphics'), href: '/graphics', icon: ShapesIcon, permission: 'graphics.read' },
                { name: t('navigation.masterRunbooks'), href: '/master-runbooks', icon: BookMarked, permission: 'master-runbooks.read' },
                { name: t('navigation.systemSettings', 'System Settings'), href: '/settings', icon: SlidersHorizontal, permission: 'system.admin' },
            ]),
        },
        {
            key: 'audit',
            label: t('navigation.adminSection.audit', 'Audit'),
            items: permitted([
                { name: t('navigation.logs', 'Logs'), href: '/logs', icon: ScrollText, permission: 'system.admin' },
            ]),
        },
    ].filter((section) => section.items.length > 0)

    return (
        <div
            className={cn(
                'flex flex-col bg-sidebar border-r border-border transition-all duration-300',
                sidebarCollapsed ? 'w-16' : 'w-64'
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Header */}
            <div className="flex items-center justify-center h-[var(--topbar-height)] border-b border-border">
                {!sidebarCollapsed && (
                    <div className="flex items-center space-x-2 px-4">
                        <Globe className="h-6 w-6 text-primary" />
                        <span className="font-semibold text-lg whitespace-nowrap">
                            {appTypeLoading ? '...' : t(`app.name.${appType}`)}
                        </span>
                    </div>
                )}
                {sidebarCollapsed && (
                    <Globe className="h-6 w-6 text-primary" />
                )}
            </div>

            {/* Navigation.
                Andrew, 2026-09-29, on a 1366×768 Windows laptop: the collapsed rail showed a scrollbar
                down its side AND one along its foot. On a short screen the list must scroll down; it
                must never scroll sideways, so it no longer can (overflow-x hidden), and its scrollbar
                is the thin one so it takes less of a 64 px rail. Windows' scrollbars take room where
                a Mac's float, and the old rows did not fit that room: 16 px padding each side left 32
                px for a row of 12 + 20 + 12, and a 17 px scrollbar left 15 px, less than the icon.
                Now 12 px padding and 10 px inside the row put the icon at the rail's centre (12 + 10
                + 20 + 10 + 12 = 64) in BOTH states - it used to move 6 px as the menu opened on hover
                - and rows start at the left rather than centring, so a scrollbar narrows the row
                without shifting the icon. */}
            <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-3 py-4 [scrollbar-width:thin]">
                {/* ── OPERATE Section ── */}
                {sectionHeading(t('navigation.operate', 'OPERATE'), true)}
                {operateItems.map(navItem)}

                {/* ── RECOVERY MANAGEMENT and MY ASSIGNMENTS ── */}
                {recoveryItems.length > 0 && (
                    <>
                        {sectionHeading(t('navigation.recoveryManagement', 'Recovery Management'))}
                        {recoveryItems.map(navItem)}
                    </>
                )}
                {assignmentItems.length > 0 && (
                    <>
                        {sectionHeading(t('navigation.myAssignments', 'My Assignments'))}
                        {assignmentItems.map(navItem)}
                    </>
                )}

                {/* ── ADMIN and SETTINGS ── */}
                {adminItems.length > 0 && (
                    <>
                        {sectionHeading(t('navigation.admin', 'Admin'))}
                        {navGroup('admin', t('navigation.admin', 'Admin'), UserCog, adminItems, adminItems.map(navItem))}
                    </>
                )}
                {settingsSections.length > 0 && (
                    <>
                        {sectionHeading(t('navigation.settings', 'Settings'))}
                        {navGroup('settings', t('navigation.settings', 'Settings'), Settings,
                            settingsSections.flatMap((section) => section.items),
                            settingsSections.map((section) => (
                                <div key={section.key} className="space-y-0.5">
                                    <div aria-hidden={sidebarCollapsed || undefined} className={cn('overflow-hidden whitespace-nowrap px-3 pt-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80', sidebarCollapsed && 'invisible')}>
                                        {section.label}
                                    </div>
                                    {section.items.map(navItem)}
                                </div>
                            )))}
                    </>
                )}
            </nav>

            {/* Footer */}
            {!sidebarCollapsed && (
                <div className="p-4 border-t border-border">
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                        <div>Environment: {import.meta.env.VITE_ENVIRONMENT || 'dev'}</div>
                        <div>Version: 1.0.0</div>
                    </div>
                </div>
            )}
        </div>
    )
}
